import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'react-hot-toast'
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { useEffect, useState } from 'react'
import { useCartStore } from './stores/cartStore'
import { usePageTracking } from './hooks/usePageTracking'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'

// Pages
import HomePage from './pages/HomePage'
import RepairPage from './pages/RepairPage'
import CheckoutPage from './pages/CheckoutPage'
import PaymentPage from './pages/PaymentPage'
import OrderConfirmationPage from './pages/OrderConfirmationPage'
import OrderTrackingPage from './pages/OrderTrackingPage'
import AdminPage from './pages/AdminPage'
import ContactPage from './pages/ContactPage'
import NotFoundPage from './pages/NotFoundPage'
import ImpressumPage from './pages/ImpressumPage'
import DatenschutzPage from './pages/DatenschutzPage'
import AgbPage from './pages/AgbPage'
import BatteriehinweisPage from './pages/BatteriehinweisPage'
import GarantiePage from './pages/GarantiePage'
import CookiePage from './pages/CookiePage'
import BlogOverviewPage from './pages/BlogOverviewPage'
import BlogDetailPageNew from './pages/BlogDetailPageNew'
import RepairDetailPage from './pages/RepairDetailPage'
import DeviceRepairsPage from './pages/DeviceRepairsPage'
// Components
import Header from './components/Header'
import Footer from './components/Footer'
import CartSidebar from './components/CartSidebar'
import CookieBanner from './components/CookieBanner'
import ScrollToTop from './components/ScrollToTop'
import MacBookServicePage from './pages/MacBookServicePage'
import MacBookNuernbergPage from './pages/MacBookNuernbergPage'
import MacBookMuenchenPage from './pages/MacBookMuenchenPage'
import SamsungDisplayReparaturPage from './pages/SamsungDisplayReparaturPage'
import SamsungAkkuTauschenPage from './pages/SamsungAkkuTauschenPage'
import MacBookStartetNichtPage from './pages/MacBookStartetNichtPage'
import MacBookWasserschadenPage from './pages/MacBookWasserschadenPage'
import MacBookAkkuTauschenPage from './pages/MacBookAkkuTauschenPage'
import MacBookLogicboardPage from './pages/MacBookLogicboardPage'
import MacBookDisplayReparaturPage from './pages/MacBookDisplayReparaturPage'
import MacBookRegensburgPage from './pages/MacBookRegensburgPage'
import IPhoneAkkuTauschenPage from './pages/IPhoneAkkuTauschenPage'
import IPhoneDisplayReparaturPage from './pages/IPhoneDisplayReparaturPage'
import IPhoneStartetNichtPage from './pages/IPhoneStartetNichtPage'
import IPhoneNeustartSchleifePage from './pages/IPhoneNeustartSchleifePage'


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Component to enable Google Analytics page tracking
function PageTracker() {
  usePageTracking()
  return null
}

function App() {
  const { items, calculateShipping } = useCartStore()
  const [paypalConfig, setPaypalConfig] = useState<{ clientId: string; mode: string }>({
    clientId: 'AdRAxoZ7bYLLD5kSAb2JoIkW-Np77LZQRK4hSy2-guFpCb-oyp30wM5oz-v8A6_78sfHyjnJZ-Bp0Gnk',
    mode: 'live'
  })

  // Fetch PayPal configuration from backend
  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
        const response = await fetch(`${apiUrl}/config/paypal`)
        const data = await response.json()

        if (data.success) {
          setPaypalConfig(data.data)
        } else {
          console.error('PayPal config API error:', data)
          // Fallback configuration if API returns error
          setPaypalConfig({
            clientId: 'AdRAxoZ7bYLLD5kSAb2JoIkW-Np77LZQRK4hSy2-guFpCb-oyp30wM5oz-v8A6_78sfHyjnJZ-Bp0Gnk',
            mode: 'live'
          })
        }
      } catch (error) {
        console.error('PayPal config fetch failed:', error)
        // Fallback configuration if backend is unreachable
        setPaypalConfig({
          clientId: 'AdRAxoZ7bYLLD5kSAb2JoIkW-Np77LZQRK4hSy2-guFpCb-oyp30wM5oz-v8A6_78sfHyjnJZ-Bp0Gnk',
          mode: 'live'
        })
      }
    }

    fetchPayPalConfig()
  }, [])

  // Recalculate shipping on app start if there are items
  useEffect(() => {
    if (items.length > 0) {
      calculateShipping()
    }
  }, []) // Run only once on app start

  return (
    <PayPalScriptProvider
      options={{
        clientId: paypalConfig.clientId,
        currency: "EUR",
        intent: "capture",
        locale: "de_DE",
        components: "buttons",
        "enable-funding": "paypal",
        "data-client-token": undefined,
        environment: paypalConfig.mode === 'live' ? 'production' : 'sandbox'
      }}
      deferLoading={false}
    >
      <QueryClientProvider client={queryClient}>
        <Router>
          <ScrollToTop />
          <PageTracker />
          <div className="min-h-screen bg-gray-50">
            <Header />
            
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/repair" element={<RepairPage />} />
                <Route path="/macbook-reparatur" element={<MacBookServicePage />} />
                <Route path="/platinen-reparatur" element={<MacBookServicePage />} />
                <Route path="/macbook-startet-nicht" element={<MacBookStartetNichtPage />} />
                <Route path="/macbook-wasserschaden" element={<MacBookWasserschadenPage />} />
                <Route path="/macbook-akku-tauschen" element={<MacBookAkkuTauschenPage />} />
                <Route path="/macbook-logicboard-reparatur" element={<MacBookLogicboardPage />} />
                <Route path="/macbook-display-reparatur" element={<MacBookDisplayReparaturPage />} />
                <Route path="/macbook-reparatur-regensburg" element={<MacBookRegensburgPage />} />
                <Route path="/macbook-reparatur-nuernberg" element={<MacBookNuernbergPage />} />
                <Route path="/macbook-reparatur-muenchen" element={<MacBookMuenchenPage />} />
                <Route path="/samsung-display-reparatur" element={<SamsungDisplayReparaturPage />} />
                <Route path="/samsung-akku-tauschen" element={<SamsungAkkuTauschenPage />} />
                <Route path="/iphone-akku-tauschen" element={<IPhoneAkkuTauschenPage />} />
                <Route path="/iphone-display-reparatur" element={<IPhoneDisplayReparaturPage />} />
                <Route path="/iphone-startet-nicht" element={<IPhoneStartetNichtPage />} />
                <Route path="/iphone-neustart-schleife" element={<IPhoneNeustartSchleifePage />} />
                <Route path="/blog" element={<BlogOverviewPage />} />
                <Route path="/blog/:slug" element={<BlogDetailPageNew />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/payment" element={<PaymentPage />} />
                <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                <Route path="/track" element={<OrderTrackingPage />} />
                <Route path="/track/:orderId" element={<OrderTrackingPage />} />
                <Route path="/adminx/*" element={<AdminPage />} />
                <Route path="/kontakt" element={<ContactPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<DatenschutzPage />} />
                <Route path="/agb" element={<AgbPage />} />
                <Route path="/batteriehinweis" element={<BatteriehinweisPage />} />
                <Route path="/garantie" element={<GarantiePage />} />
                <Route path="/cookies" element={<CookiePage />} />

                {/* SEO-optimized repair routes - must be after static routes */}
                <Route path="/:brandSlug/:deviceSlug/:repairSlug" element={<RepairDetailPage />} />
                <Route path="/:brandSlug/:deviceSlug" element={<DeviceRepairsPage />} />

                {/* 404 Catch-all route - must be last */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            
            <Footer />
            
            {/* Cart Sidebar */}
            <CartSidebar />
            
            {/* Cookie Banner */}
            <CookieBanner />
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />

            {/* Vercel Analytics & Speed Insights */}
            <Analytics />
            <SpeedInsights />
          </div>
        </Router>
        
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </PayPalScriptProvider>
  )
}

export default App